import { browser } from '$app/environment';
import { get } from 'svelte/store';
import { Sentry } from '$lib/services/sentry';
import { ACTIVE_EXPERIMENTS } from '$lib/services/experiments/client/constants';
import { experimentClient } from '$lib/services/experiments/client_v2';
import { app } from '$lib/stores/app/store';
import {
	buildPaymentContext,
	createPaymentSession,
	createSignifydLoader,
	type PaymentContext,
	type SignifydHooks,
} from '@mkt-client/payment-providers/signifyd';

/**
 * Classic-client Signifyd adapter.
 *
 * Wires the framework-agnostic `@mkt-client/payment-providers/signifyd` module to
 * SvelteKit's browser flag, `sessionStorage`, the `$app` store, and Sentry. The
 * shared module owns the logic (session id, script injection, payment_context
 * assembly); this file only injects the classic-specific dependencies — mirroring
 * how the PayPal adapter injects `getDeviceId`/hooks. See TAD ABC2-17602.
 */

/**
 * ONE payment-session per client, shared between the loader (which stamps the id
 * onto the Signifyd script's `data-order-session-id`) and `buildClassicPaymentContext`
 * (which sends the same id as `payment_context.payment_session_id`), so the browser
 * fingerprint and the transaction correlate server-side.
 */
const session = createPaymentSession({
	isBrowser: browser,
	storage: browser && typeof sessionStorage !== 'undefined' ? sessionStorage : null,
});

/** Telemetry hooks — the shared loader stays free of Sentry; classic wires its own. */
const hooks: SignifydHooks = {
	onSkip: ({ reason }) => {
		// A missing session id should be impossible in the browser (the session util always
		// mints one). If it happens, device profiling silently won't run AND the payload's
		// payment_context.payment_session_id is lost — surface it rather than bury a breadcrumb.
		Sentry.captureMessage(`Signifyd device-profiling skipped: ${reason}`, {
			level: 'warning',
			extra: { reason },
		});
	},
	onScriptLoaded: () => {
		Sentry.addBreadcrumb({
			category: 'signifyd',
			level: 'info',
			message: 'device-profiling script loaded',
		});
	},
	onScriptError: ({ src }) => {
		Sentry.captureMessage('Signifyd device-profiling script failed to load', {
			level: 'error',
			extra: { src },
		});
	},
	onSigScriptError: ({ detail }) => {
		Sentry.captureMessage('Signifyd device-profiling failed (sigScriptError)', {
			level: 'error',
			extra: { detail },
		});
	},
};

const loader = createSignifydLoader({
	getSessionId: () => session.getPaymentSessionId(),
	hooks,
});

/**
 * Injects the Signifyd device-profiling script into `<head>`. No-ops during SSR
 * and is idempotent across A/B variant remounts. Wrapped so a synchronous injection
 * failure (CSP, a null `document.head`, a DOM-monkeypatching extension, or the
 * session util throwing) is reported to Sentry and never propagates into the
 * payment page's `onMount` — device profiling is best-effort and must not break checkout.
 * @returns {void}
 */
export const loadSignifyd = (): void => {
	try {
		loader.load();
	} catch (err) {
		Sentry.captureException(err, { extra: { action: 'Signifyd loadSignifyd' } });
	}
};

/**
 * Removes the Signifyd script and detaches its error listener. Safe to call when
 * nothing was injected.
 * @returns {void}
 */
export const removeSignifyd = (): void => loader.remove();

/**
 * Returns the current payment-session id (UUID v4, 1-hour TTL), minting one when
 * needed. Stable within the session so the script tag and the payload agree.
 * @returns {string}
 */
export const getPaymentSessionId = (): string => session.getPaymentSessionId();

/**
 * Clears the payment-session id (memory + sessionStorage) so the next call mints anew.
 * @returns {void}
 */
export const resetPaymentSessionId = (): void => session.resetPaymentSessionId();

/**
 * Assembles the classic checkout's `payment_context`, pairing the current
 * payment-session id with the device context (`$app.meta`) and the caller-supplied
 * username/phone. Returns `undefined` when no session id is available so the payload
 * builder can spread it conditionally and keep the field additive.
 * @param {object} [fields] - caller-supplied identity fields
 * @param {string} [fields.username] - the account username
 * @param {string} [fields.email] - the contact email (sits under contact_info.email)
 * @param {string} [fields.phone] - the contact phone number
 * @returns {PaymentContext | undefined} the assembled payment_context, or undefined
 */
export const buildClassicPaymentContext = (
	fields: { username?: string; email?: string; phone?: string } = {}
): PaymentContext | undefined =>
	buildPaymentContext({
		sessionId: getPaymentSessionId(),
		username: fields.username,
		email: fields.email,
		phone: fields.phone,
		deviceProfiling: { ...(get(app)?.meta ?? {}) },
	});

/**
 * Signifyd pre-auth experiment (ABC2-17607) — in-scope primary checkout only
 * (new-subscription, reactivation, alt-payment, special-offer). Assigns Control vs Test and records
 * exposure at checkout-page load, before the payment form + device-profiling script
 * mount. `session_id` is the Signifyd payment-session UUID so assignment, device
 * fingerprint, and transaction correlate on one id. On failure the arm is absent and
 * callers fall back to Control (BAU).
 * @returns {Promise<void>}
 */
export async function initializeSignifydPreAuthExperiment(): Promise<void> {
	try {
		await experimentClient.getAssignment({
			autoMarkSeen: true,
			context: { sessionId: getPaymentSessionId() },
			experiment: ACTIVE_EXPERIMENTS.ABCM2_SIGNIFYD_V2,
		});
	} catch {
		/* fall back to Control — isSignifydPreAuthTestArm() stays false */
	}
}

/**
 * Whether the Signifyd pre-auth Test cohort is active. True only after a successful
 * assignment on an in-scope checkout page; false for Control and every out-of-scope flow.
 * @returns {boolean}
 */
export function isSignifydPreAuthTestArm(): boolean {
	const { experimentKey, variants } = ACTIVE_EXPERIMENTS.ABCM2_SIGNIFYD_V2;
	return experimentClient.isVariantActive(experimentKey, variants.test);
}
